/**
 * AISidebar — Global floating AI action panel.
 *
 * Renders a FAB (bottom-right) that opens a Sheet with context-aware
 * AI capabilities driven by the page's `aiCapabilities` config.
 * Feature-gated by `ai_suite`.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Brain, ArrowLeft, Loader2, Presentation } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { useToast } from '@/hooks/use-toast'
import { useFeatureEnabled, FEATURE_KEYS } from '@/hooks/useFeatures'
import { useCurrentStudy } from '@/stores/studyStore'
import { useAISidebarStore } from '@/stores/aiSidebarStore'
import { useNavigationStore } from '@/stores/navigationStore'
import {
  fetchAICapabilities,
  triggerSlideGeneration,
  getSlideStatus,
  downloadSlides,
} from '@/lib/aiSuiteApi'
import type { AICapabilityDefinition, AIAudienceDefinition } from '@/lib/aiSuiteApi'

// =============================================================================
// Slide Generation Panel (reused for all audience types)
// =============================================================================

interface SlideGenerationPanelProps {
  studyId: string
  meetingType: string
  meetingLabel: string
  meetingDescription: string
  pageKey?: string
}

function SlideGenerationPanel({
  studyId,
  meetingType,
  meetingLabel,
  meetingDescription,
  pageKey,
}: SlideGenerationPanelProps) {
  const { toast } = useToast()
  const [status, setStatus] = useState<'idle' | 'generating' | 'downloading' | 'done' | 'error'>(
    'idle'
  )
  const [errorMsg, setErrorMsg] = useState('')
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const handleGenerate = useCallback(async () => {
    setStatus('generating')
    setErrorMsg('')

    try {
      const result = await triggerSlideGeneration(studyId, meetingType, { pageKey })
      const taskId = result.taskId

      pollRef.current = setInterval(async () => {
        try {
          const statusResult = await getSlideStatus(studyId, taskId)

          if (statusResult.status === 'completed') {
            if (pollRef.current) clearInterval(pollRef.current)
            pollRef.current = null

            setStatus('downloading')
            await downloadSlides(studyId, taskId)

            setStatus('done')
            toast({
              title: 'Slides generated',
              description: 'PPTX file downloaded successfully.',
            })
          } else if (statusResult.status === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current)
            pollRef.current = null

            setStatus('error')
            setErrorMsg(statusResult.error || 'An error occurred.')
            toast({
              title: 'Generation failed',
              description: statusResult.error || 'An error occurred.',
              variant: 'destructive',
            })
          }
        } catch {
          // Poll error — continue polling
        }
      }, 3000)
    } catch (err) {
      setStatus('error')
      const msg = err instanceof Error ? err.message : 'An error occurred.'
      setErrorMsg(msg)
      toast({
        title: 'Failed to start generation',
        description: msg,
        variant: 'destructive',
      })
    }
  }, [studyId, meetingType, pageKey, toast])

  return (
    <div className="space-y-4">
      <div className="p-4 bg-muted rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Presentation className="h-5 w-5 text-primary" />
          <span className="font-medium">{meetingLabel}</span>
        </div>
        <p className="text-sm text-muted-foreground">{meetingDescription}</p>
      </div>

      {status === 'idle' && (
        <Button onClick={handleGenerate} className="w-full">
          <Presentation className="h-4 w-4 mr-2" />
          Generate Slides
        </Button>
      )}

      {status === 'generating' && (
        <div className="flex items-center justify-center gap-2 p-4 bg-muted/50 rounded-lg">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm">Generating slides...</span>
        </div>
      )}

      {status === 'downloading' && (
        <div className="flex items-center justify-center gap-2 p-4 bg-muted/50 rounded-lg">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm">Downloading...</span>
        </div>
      )}

      {status === 'done' && (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg text-green-700 dark:text-green-400">
            <span className="text-sm font-medium">Slides downloaded successfully!</span>
          </div>
          <Button variant="outline" onClick={() => setStatus('idle')} className="w-full">
            Generate Again
          </Button>
        </div>
      )}

      {status === 'error' && (
        <div className="space-y-3">
          <div className="p-4 bg-destructive/10 rounded-lg text-destructive">
            <p className="text-sm">{errorMsg}</p>
          </div>
          <Button variant="outline" onClick={handleGenerate} className="w-full">
            Retry
          </Button>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Capability Card
// =============================================================================

interface CapabilityCardProps {
  capability: AICapabilityDefinition
  onClick: () => void
}

function CapabilityCard({ capability, onClick }: CapabilityCardProps) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 p-2 rounded-md bg-primary/10">
          <Presentation className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{capability.label}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{capability.description}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {capability.audiences.length} audience{capability.audiences.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </button>
  )
}

// =============================================================================
// Audience Selector
// =============================================================================

interface AudienceSelectorProps {
  capability: AICapabilityDefinition
  studyId: string
  pageKey?: string
}

function AudienceSelector({ capability, studyId, pageKey }: AudienceSelectorProps) {
  const [selectedAudience, setSelectedAudience] = useState<AIAudienceDefinition | null>(null)

  if (selectedAudience) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedAudience(null)}
          className="gap-1 -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to audiences
        </Button>
        {capability.key === 'slide_generation' && (
          <SlideGenerationPanel
            studyId={studyId}
            meetingType={selectedAudience.key}
            meetingLabel={selectedAudience.label}
            meetingDescription={selectedAudience.description}
            pageKey={pageKey}
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Select an audience type:</p>
      {capability.audiences.map((audience) => (
        <button
          key={audience.key}
          onClick={() => setSelectedAudience(audience)}
          className="w-full text-left p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
        >
          <div className="font-medium text-sm">{audience.label}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{audience.description}</div>
        </button>
      ))}
    </div>
  )
}

// =============================================================================
// Helper: find current page's aiCapabilities from navigation store
// =============================================================================

function useCurrentPageCapabilities(pageKey: string | undefined): string[] {
  const studyNavigation = useNavigationStore((state) => state.studyNavigation)

  if (!pageKey) return []

  // Search through nav tree for the matching page
  function findCapabilities(items: ReturnType<typeof useNavigationStore.getState>['studyNavigation']): string[] | null {
    for (const item of items) {
      if (item.moduleKey === pageKey) {
        return item.aiCapabilities ?? []
      }
      if (item.children) {
        const found = findCapabilities(item.children)
        if (found) return found
      }
    }
    return null
  }

  return findCapabilities(studyNavigation) ?? []
}

// =============================================================================
// Main AISidebar
// =============================================================================

export function AISidebar() {
  const { studyId: urlStudyId, pageKey } = useParams<{ studyId: string; pageKey: string }>()
  const currentStudy = useCurrentStudy()
  const studyId = urlStudyId || currentStudy?.studyId
  const aiEnabled = useFeatureEnabled(studyId, FEATURE_KEYS.AI_SUITE)

  const { isOpen, activeAction, open, close, setActiveAction } = useAISidebarStore()

  // Get current page's enabled capabilities from navigation store
  const pageAiCapabilities = useCurrentPageCapabilities(pageKey)

  // Fetch capability definitions from backend
  const { data: capabilitiesData } = useQuery({
    queryKey: ['ai-capabilities', studyId],
    queryFn: () => fetchAICapabilities(studyId!),
    enabled: !!studyId && aiEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes — capability definitions rarely change
  })

  // Filter capabilities to only those enabled on the current page
  const enabledCapabilities = (capabilitiesData?.capabilities ?? []).filter((cap) =>
    pageAiCapabilities.includes(cap.key)
  )

  const handleSelectCapability = (capKey: string) => {
    setActiveAction(capKey)
  }

  const handleBack = () => {
    setActiveAction(null)
  }

  // Don't render if no study, AI not enabled, or no capabilities on this page
  if (!studyId || !aiEnabled || enabledCapabilities.length === 0) {
    return null
  }

  const selectedCapability = activeAction
    ? enabledCapabilities.find((c) => c.key === activeAction)
    : null

  return (
    <>
      {/* FAB Button */}
      {!isOpen && (
        <button
          onClick={open}
          className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105 flex items-center justify-center"
          aria-label="Open AI Assistant"
        >
          <Brain className="h-6 w-6" />
          {enabledCapabilities.length > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
              {enabledCapabilities.length}
            </span>
          )}
        </button>
      )}

      {/* Sheet Panel */}
      <Sheet open={isOpen} onOpenChange={(o) => !o && close()}>
        <SheetContent
          className="w-full sm:max-w-md overflow-y-auto"
          aria-describedby="ai-sidebar-description"
        >
          <SheetHeader>
            <div className="flex items-center gap-2">
              {selectedCapability && (
                <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <SheetTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                {selectedCapability ? selectedCapability.label : 'AI Assistant'}
              </SheetTitle>
            </div>
            <SheetDescription id="ai-sidebar-description">
              {selectedCapability
                ? selectedCapability.description
                : 'AI-powered actions for the current page'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6">
            {selectedCapability ? (
              /* Capability Detail — audience selector */
              <AudienceSelector
                capability={selectedCapability}
                studyId={studyId}
                pageKey={pageKey}
              />
            ) : (
              /* Capability List */
              <div className="space-y-3">
                {enabledCapabilities.length > 0 ? (
                  enabledCapabilities.map((cap) => (
                    <CapabilityCard
                      key={cap.key}
                      capability={cap}
                      onClick={() => handleSelectCapability(cap.key)}
                    />
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Brain className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      No AI capabilities enabled for this page
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      An admin can enable AI capabilities in the navigation editor
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

export default AISidebar
